import clsx from 'clsx';
import React, { useState } from 'react';
import ReactHtmlParser from 'react-html-parser';

import EmailLeadCapture from '@components/EmailLeadCapture';
import useEventTracking from '@hooks/useEventTracking';
import useWindowSize from '@hooks/useWindowSize';
import { Theme } from '@interfaces/global';
import { subscribe_path } from '@routes';
import { READER_URL } from '@src/env';
import { ClickTarget, TrackingLocation, click } from '@tracking';
import { WIDTH_SM } from '@utils/constants';
import { FormLocation, LeadTypes } from '@utils/enums';

import { PaywallProps } from '..';
import OfferOption, { SelectedOffer } from '../OfferOption';

const PaywallForm = ({
  articleId,
  briefingGuid,
  buttonText = 'Get started',
  isPro: isProArticle,
  isTwoOptPaywall,
  leadDetails,
  leadType = LeadTypes.ArticleLead,
  offerLink,
  paywallLeadOffers,
  shareGuid,
  skipVerifyNewEmail,
  subtitle,
  theme,
  title,
  unlockContent,
}: PaywallProps): JSX.Element => {
  const { width: windowWidth } = useWindowSize();
  const { trackEvent } = useEventTracking({ location: TrackingLocation.Paywall });
  const [selectedOption, setSelectedOption] = useState<SelectedOffer>(
    paywallLeadOffers?.[0] || null
  );

  const redirectToOnSuccess = selectedOption
    ? subscribe_path({ offer: selectedOption.code })
    : offerLink;
  const oauthRedirectToOnSuccess = `${READER_URL}${redirectToOnSuccess}`;

  const withOffers = paywallLeadOffers?.length > 0;
  const isProOffer = (isProArticle && !withOffers) || selectedOption?.isPro;

  const handleOptionSelection = (offer: SelectedOffer) => {
    setSelectedOption(offer);
    trackEvent(click({ clickTarget: ClickTarget.Offer, offerCode: offer.code }));
  };

  return (
    <div className="flex w-full flex-col flex-nowrap content-center items-center justify-center px-24 sm:px-64 sm:pb-32 md:justify-between">
      <header
        className={clsx('flex w-full flex-col text-left', {
          'sm:w-[610px]': !isProOffer,
          'sm:w-[660px]': isProOffer,
          'sm:text-center': theme == Theme.Light,
          'text-center': theme == Theme.Dark,
        })}
      >
        <h1
          className={clsx(
            'my-0 font-suisse-neue text-subheadline-sm sm:font-suisse-works sm:text-headline-lg',
            {
              'text-black sm:text-white': theme == Theme.Light,
              'text-white': theme == Theme.Dark,
            }
          )}
        >
          {title}
        </h1>
        {subtitle && !isTwoOptPaywall && (
          <p className="my-0 hidden font-suisse-intl-mono text-overline-lg text-black sm:mt-8 sm:block sm:text-white">
            {ReactHtmlParser(subtitle)}
          </p>
        )}
      </header>
      {isTwoOptPaywall && !!selectedOption && (
        <>
          <div className="mt-16 flex flex-col items-center justify-start gap-16 sm:mt-8 sm:flex-row sm:items-start sm:gap-32">
            {paywallLeadOffers?.map((offer, index) => (
              <OfferOption
                key={index}
                offer={offer}
                selectedOfferCode={selectedOption.code}
                setSelectedOffer={handleOptionSelection}
              />
            ))}
          </div>
          <div className="mt-32 hidden h-[0.5px] w-[610px] bg-grayscale-beige sm:block"></div>
        </>
      )}
      <div className="mt-16 w-full sm:mt-24 sm:w-[516px]">
        <div className="sm:row-auto sm:grid sm:grid-flow-row sm:grid-cols-2 sm:gap-x-16">
          <EmailLeadCapture
            articleId={articleId}
            briefingGuid={briefingGuid}
            buttonClassName="w-full sm:w-auto"
            buttonText={buttonText}
            formClassName="mb-0 sm:col-span-2 sm:mb-20"
            formLocation={FormLocation.Paywall}
            gdprClassName="mb-16"
            inputClassName="w-full"
            isPro={isProOffer}
            isVertical={windowWidth < WIDTH_SM}
            leadDetails={leadDetails}
            leadType={leadType}
            oauthRedirectToOnSuccess={oauthRedirectToOnSuccess}
            redirectToOnSuccess={redirectToOnSuccess}
            shareGuid={shareGuid}
            signInOptionClass="text-center sm:text-left"
            skipVerifyNewEmail={skipVerifyNewEmail}
            theme={theme}
            unlockContent={unlockContent}
            withOauth
          />
        </div>
      </div>
    </div>
  );
};

export default PaywallForm;
